import "./component-cart.scss";
import { html, nothing } from "lit";
import ComponentBase from "./component-base";
import { Orders, Receipts } from "../assets/js/libs/api";
import ConfirmationDialog from "./confirmation-dialog";
import ComponentQuantitySelector from "./component-quantity-selector";
import ComponentAlert, { ALERT_TYPES } from "./component-alert";
import PaymentModal from "./payment-modal";
import {
  IsChangeModalEnabled,
  IsQrCodeReaderEnabled,
} from "../assets/js/libs/utils";
import { ProductDto } from "../model/dtos";
import "./component-cart-item";
import { property } from "lit/decorators.js";
import store from "../webapps/shop/redux/store";
import {
  emptyCart,
  removeItemFromCart,
  selectItemsFromCart,
  updateCartItemQty,
} from "../webapps/shop/redux/actions";
import QrCodeScanModal from "./qrcode-scan-modal";

class ComponentCart extends ComponentBase {
  static get tagName() {
    return "component-cart";
  }

  @property()
  items: [];
  @property()
  isSubmitting: boolean;
  @property()
  isSubmittingGift: boolean;

  constructor() {
    super();
    this.items = selectItemsFromCart();
    this.OnQtyChange = this.OnQtyChange.bind(this);
    this.OnQtyBlur = this.OnQtyBlur.bind(this);
    this.OnQtyClick = this.OnQtyClick.bind(this);
    this.OnIncreaseQty = this.OnIncreaseQty.bind(this);
    this.OnDecreaseQty = this.OnDecreaseQty.bind(this);
    this.OnRemoveItem = this.OnRemoveItem.bind(this);
    this.isSubmitting = false;
    this.isSubmittingGift = false;
  }

  connectedCallback(): void {
    super.connectedCallback();
    store.subscribe(() => {
      const items = selectItemsFromCart();
      const cartIcon = this.querySelector(".ncx-shop-cart__row .bi-bag");
      if (cartIcon) {
        const oldCount = this.items.reduce(
          (acc, item) => acc + item.quantity,
          0
        );
        const newCount = items.reduce((acc, item) => acc + item.quantity, 0);
        if (newCount > oldCount) {
          if (this.cartIconAnimationTimeout) {
            this.querySelector(".ncx-shop-cart__row .bi-bag").classList.remove(
              "added-to-cart"
            );
            clearTimeout(this.cartIconAnimationTimeout);
          }
          this.querySelector(".ncx-shop-cart__row .bi-bag").classList.add(
            "added-to-cart"
          );
          this.cartIconAnimationTimeout = setTimeout(() => {
            this.querySelector(".ncx-shop-cart__row .bi-bag").classList.remove(
              "added-to-cart"
            );
          }, 1500);
        }
      }
      this.items = [...items];
    });
  }

  get total() {
    const total = this.items.reduce((sum, currItem) => {
      let price = currItem.price;
      if (currItem.customizations) {
        currItem.customizations.forEach((c) =>
          c.customizations.forEach((cc) => (price += cc.price))
        );
      }
      return sum + currItem.quantity * price;
    }, 0);
    return total;
  }

  OnRemoveItem(index, quantity = 1) {
    removeItemFromCart(index, quantity);
  }

  formatPrice() {
    return Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    });
  }

  OnQtyChange(e, index) {
    const qty = Number(e.currentTarget.value);
    updateCartItemQty(index, qty);
  }

  OnQtyBlur(e, index) {
    const qty = Number(e.currentTarget.value);
    if (qty < 1) {
      e.currentTarget.value = 1;
      updateCartItemQty(index, 1);
    }
  }

  async OnQtyClick(e, index) {
    const qty = await ComponentQuantitySelector.Show(
      selectItemsFromCart()[index].quantity
    );
    if (isNaN(qty) || qty < 1) return;

    updateCartItemQty(index, qty);
  }

  OnDecreaseQty(e, index) {
    const currentQty = selectItemsFromCart()[index].quantity;
    if (currentQty == 1) {
      return;
    }
    updateCartItemQty(index, currentQty - 1);
  }

  OnIncreaseQty(e, index) {
    const currentQty = selectItemsFromCart()[index].quantity;
    updateCartItemQty(index, currentQty + 1);
  }

  async SendOrder(gift = false) {
    try {
      const total = this.total;
      if (!gift) {
        if (IsChangeModalEnabled()) {
          await PaymentModal.Show(total);
        } else {
          await ConfirmationDialog.Show({
            heading: "Attesa Pagamento...",
            message: html`
              <div class="mb-2">
                Totale da pagare:
                <big
                  ><strong
                    >${gift
                      ? "Omaggio"
                      : this.formatPrice().format(total)}</strong
                  ></big
                >
              </div>
            `,
            confirmBtnText: "Pagato",
          });
        }
      } else {
        await ConfirmationDialog.Show({
          heading: "Conferma Omaggio",
          message: "Vuoi inviare l'ordine omaggio?",
        });
      }
      if (gift) this.isSubmittingGift = true;
      else this.isSubmitting = true;
      document.body.classList.add("ui-locked");
      this.requestUpdate();
      var OrderProducts = new Array();
      this.items.forEach((i) => {
        OrderProducts.push({
          product_id: i.id,
          quantity: i.quantity,
          customizations:
            i.customizations?.map((c) => ({
              ["" + c.customizationId]: c.customizations.map((_) => _.id),
            })) || [],
        });
      });
      try {
        const { id } = (await Orders.Create(OrderProducts, gift)).data;
        emptyCart();
        ConfirmationDialog.Show({
          heading: "Ordine Inviato",
          message: html`
            Ordine inviato con successo.
            <br />
            <br />
            <button
              @click=${() => this.Reprint(id)}
              class="btn btn-secondary btn-lg"
            >
              Ristampa
            </button>
          `,
          confirmBtnText: "Chiudi",
          cancelBtn: false,
        });
      } catch (error) {
        console.error(error);
        ComponentAlert.Show({
          msg: "Ordine non inviato. Riprova.",
          type: ALERT_TYPES.warning,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isSubmitting = false;
      this.isSubmittingGift = false;
      document.body.classList.remove("ui-locked");
      this.requestUpdate();
    }
  }

  async Reprint(iD) {
    try {
      await ConfirmationDialog.Show({
        heading: "Ristampa",
        message: "Vuoi ristampare lo scontrino?",
      });
      try {
        await Receipts.Print(iD);
      } catch (error) {
        console.error(error);
        ComponentAlert.Show({
          msg: "Errore. Riprova.",
          type: ALERT_TYPES.warning,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  render() {
    return html`
      <div class="total pb-2">
        <div class="fs-4 mb-1">
          Totale: ${this.formatPrice().format(this.total)}
        </div>
        ${this.isSubmitting
          ? html`
              <button
                class="w-100 btn btn-primary btn-lg"
                type="button"
                disabled
              >
                <span
                  class="spinner-grow spinner-grow-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                STAMPA...
              </button>
            `
          : html`
              <button
                type="button"
                @click=${() => this.SendOrder()}
                class="w-100 btn btn-primary btn-lg"
                disabled=${this.items.length < 1 ||
                this.isSubmittingGift ||
                nothing}
              >
                SCONTRINO <i class="bi bi-printer"></i>
              </button>
            `}
        ${this.isSubmittingGift
          ? html`
              <button
                class="w-100 mt-3 btn btn-primary btn-lg btn-success"
                type="button"
                disabled
              >
                <span
                  class="spinner-grow spinner-grow-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                STAMPA...
              </button>
            `
          : html`
              <button
                type="button"
                @click=${() => this.SendOrder(true)}
                class="w-100 mt-3 btn btn-primary btn-lg btn-success"
                disabled=${this.items.length < 1 ||
                this.isSubmitting ||
                nothing}
              >
                OMAGGIO <i class="bi bi-printer"></i>
              </button>
            `}
        ${IsQrCodeReaderEnabled()
          ? html`<button
              @click=${() => {
                this.ScanQrCode();
              }}
              class="w-100 mt-3 btn btn-lg btn-secondary"
            >
              CODICE QR
              <i class="bi bi-qr-code-scan"></i>
            </button>`
          : nothing}
      </div>
      ${this.RenderItems()}
    `;
  }

  async ScanQrCode() {
    const res = await QrCodeScanModal.Show();
    console.log(res);
  }

  RenderItems() {
    return html`
      <div class="items-wrapper">
        ${this.items.map(
          (item: ProductDto & { customizations: [] }, index) => html`
            <ncx-cart-item
              .item=${{ ...item }}
              .index=${index}
              .OnQtyChange=${this.OnQtyChange}
              .OnQtyBlur=${this.OnQtyBlur}
              .OnQtyClick=${this.OnQtyClick}
              .OnIncreaseQty=${this.OnIncreaseQty}
              .OnDecreaseQty=${this.OnDecreaseQty}
              .OnRemoveItem=${this.OnRemoveItem}
            ></ncx-cart-item>
          `
        )}
      </div>
    `;
  }
}
ComponentCart.Register();
export default ComponentCart;
